import { CommonTooltips } from "@mcleod/common";
import { BlurEvent, Button, ClickEvent, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, PrintableEvent, SlideoutDecorator, TableAction, TableContentsChangedEvent, TableRow, TableRowBeforeSaveEvent, TableRowDisplayEvent, TableRowMode, Textbox, ValidationResult } from "@mcleod/components";
import { Alignment, Api, DatePart, DateUtil, ModelRow, StringUtil } from "@mcleod/core";
import { FilterPanel, SecondaryFilterProps } from "@mcleod/dispatch";
import { getDispatchControlValue } from "@mcleod/general";
import { AutogenLayoutTableRateExceptions } from "./autogen/AutogenLayoutTableRateExceptions";
import { RateExceptionDuplication } from "./RateExceptionDuplication";

const ACTIVE: SecondaryFilterProps = {
    id: "active",
    caption: "Active",
    filter: (row: ModelRow) => {
        const currentTimestamp = new Date();
        currentTimestamp.setHours(23,59,59);
        const expireTimestamp = new Date(row.get("expire_timestamp"));
        return row.get("expire_timestamp") == null || currentTimestamp < expireTimestamp;
    }
}

const EXPIRED: SecondaryFilterProps = {
    id: "expired",
    caption: "Expired",
    filter: (row: ModelRow) => {
        const currentTimestamp = new Date();
        currentTimestamp.setHours(23,59,59);
        const expireTimestamp = new Date(row.get("expire_timestamp"));
        return row.get("expire_timestamp") != null && currentTimestamp >= expireTimestamp;
    }
}

export class TableRateExceptions extends AutogenLayoutTableRateExceptions {
    private validGlaUidsForRateExceptions: string;
    private validCalcUomCodesForRateExceptions: string;
    private filterPanel: FilterPanel;

    override async onLoad() {
        this.validGlaUidsForRateExceptions = (await Api.search("lme/dispatch/gl-assignment", { "allow_for_rate_exceptions": "Y" })).data.map((row) => row.gla_uid).join(",");
        this.validCalcUomCodesForRateExceptions = getDispatchControlValue("rate_exception_uom_list")?.replace(/ /g, "") ?? "";
        this.filterPanel = new FilterPanel([ACTIVE, EXPIRED], this.tableRateExceptions, this.panelSecondaryFilters);
        this.addTableListener();
    }

    tableRateExceptionsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const shipperTextbox = tableRow.findComponentById("textboxShipRegName") as Textbox;
        const consigneeTextbox = tableRow.findComponentById("textboxConsRegName") as Textbox;
        const effectiveTextbox = tableRow.findComponentById("textboxEffectiveTimestamp") as Textbox;
        const expirationTextbox = tableRow.findComponentById("textboxExpirationDate") as Textbox;
        const methodTextbox = tableRow.findComponentById("textboxMethod") as Textbox;
        const effectiveTimeStampTextbox = tableRow.findComponentById("textboxEffectiveTimestamp");
        if (tableRow.mode !== TableRowMode.QUICK_ADD) {
            effectiveTimeStampTextbox.enabled = false;
            const shipperRegionName = tableRow.data.get("ship_reg_name");
            const consigneeRegionName = tableRow.data.get("cons_reg_name");
            const layoutPath = "lme/dispatch/TariffRegionQuickInfo";
            if (!StringUtil.isEmptyString(shipperRegionName)) {
                CommonTooltips.setTooltipContentLayout(shipperTextbox, null, layoutPath, null, { region_name: shipperRegionName });
            }
            if (!StringUtil.isEmptyString(consigneeRegionName)) {
                CommonTooltips.setTooltipContentLayout(consigneeTextbox, null, layoutPath, null, { region_name: consigneeRegionName });
            }
            const calcUomCode = tableRow.data.get("calc_uom_code");
            if (!StringUtil.isEmptyString(calcUomCode)) {
                CommonTooltips.setTooltipContentLayout(methodTextbox, null, "lme/dispatch/ScreenCodesQuickInfo", null, 
                    { field_code: calcUomCode, field_name: "calc_uom_code" });
            }
        }
        const expirationDate = DateUtil.parseDateWithKeywords(expirationTextbox.text, true, true);
        const todaysDate = DateUtil.parseDate(new Date());
        const dayDiff = DateUtil.dateDiff(DatePart.SECOND, expirationDate, todaysDate);
        if (expirationDate != null && dayDiff < 0) {
            expirationTextbox.enabled = false;
        }
        else {
            effectiveTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                return this.dateValidator(effectiveTextbox, expirationTextbox, true, tableRow, mode);
            };
            expirationTextbox.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                return this.dateValidator(effectiveTextbox, expirationTextbox, false, tableRow, mode);
            };
        }
    }

    addTableListener() {
        this.tableRateExceptions.addRowBeforeSaveListener((event: TableRowBeforeSaveEvent) => {
            const tableRow = event.getTableRow();
            if (tableRow.data._appending && tableRow.mode === TableRowMode.QUICK_ADD && tableRow.validateSimple()) {
                tableRow.data.set("priority", 500000);
                const duplicateRows = this.checkForDuplicates(tableRow);
                if (duplicateRows.length > 0) {
                    this.showSlideout(tableRow, duplicateRows);
                }
            }
        });
    }

    private dateValidator(effectiveTextbox: Textbox, expirationTextbox: Textbox, isEffective: boolean,
        updatedTableRow: TableRow, mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if (!StringUtil.isEmptyString(effectiveTextbox.text) && !StringUtil.isEmptyString(expirationTextbox.text)) {
            const effectiveDate = DateUtil.parseDateWithKeywords(effectiveTextbox.text, true, true);
            const expirationDate = DateUtil.parseDateWithKeywords(expirationTextbox.text, true, true);
            const dayDiff = DateUtil.dateDiff(DatePart.SECOND, expirationDate, effectiveDate);

            if (dayDiff <= 0) {
                return {
                    component: isEffective ? effectiveTextbox : expirationTextbox,
                    isValid: false,
                    validationMessage: "The effective date cannot be the same or later than the expiration date."
                };
            }
        }
        if (updatedTableRow.mode !== TableRowMode.QUICK_ADD && this.checkForDuplicates(updatedTableRow).length > 0) {
            return {
                component: isEffective ? effectiveTextbox : expirationTextbox,
                isValid: false,
                validationMessage: "The effective/expiration date can not overlap a duplicate rate exception."
            };
        }
        return null;
    }

    checkForDuplicates(rowToCheck: TableRow): TableRow[] {
        const currentTimestamp = new Date();
        currentTimestamp.setHours(23,59,59);
        const newRow = rowToCheck.data as ModelRow;
        const duplicates: TableRow[] = [];
        this.tableRateExceptions.allRows.forEach((currentRow: TableRow) => {
            const currentModelRow = currentRow.data as ModelRow;
            const expireTimestamp = new Date(currentModelRow.get("expire_timestamp"));
            if ((rowToCheck.data._appending || currentRow.index !== rowToCheck.index) && (currentModelRow.get('expire_timestamp') == null || expireTimestamp > currentTimestamp)) {
                if (currentModelRow.get("gla_uid") == newRow.get("gla_uid") && currentModelRow.get("carrier_scac") == newRow.get("carrier_scac")
                    && currentModelRow.get("ship_reg_name") == newRow.get("ship_reg_name") && currentModelRow.get("cons_reg_name") == newRow.get("cons_reg_name")
                    && currentModelRow.get("charge_regardless_flag") == newRow.get("charge_regardless_flag")
                    && currentModelRow.get('reversible_flag') == newRow.get('reversible_flag') && currentModelRow.get('calc_uom_code') == newRow.get('calc_uom_code')
                    && (currentModelRow.get("tli_uid") == null && currentModelRow.get("tli_uid") == null || currentModelRow.get("tli_uid") != newRow.get("tli_uid"))) {
                    const effective1 = currentModelRow.get('effective_timestamp');
                    const expire1 = currentModelRow.get('expire_timestamp');
                    const effective2 = newRow.get('effective_timestamp');
                    const expire2 = newRow.get('expire_timestamp');
                    if (!((expire1 != null || expire2 != null) && ((expire1 != null && Date.parse(expire1) < Date.parse(effective2)) || Date.parse(expire2) != null && Date.parse(expire2) < Date.parse(effective1)))) {
                        duplicates.push(currentRow)
                    }
                }
            }
        });
        return duplicates;
    }

    showSlideout(newRow: TableRow, currentRows: TableRow[]) {
        const layout = Layout.getLayout("lme/powerbroker/RateExceptionDuplication") as RateExceptionDuplication;
        new SlideoutDecorator({
            layout: layout,
            width: "60%",
            onClose: null,
            layoutLoadListeners: async () => {
                const newModelRow = newRow.data;
                newModelRow.set("type", "New");
                currentRows.forEach((currentRow: TableRow) => {
                    const currentCopy = (currentRow.data as ModelRow).createBasicCopy();
                    currentCopy.set("type", "Existing");
                    const newRowEffective = DateUtil.parseDate(newModelRow.get("effective_timestamp"))
                    const dateOneDayBefore = DateUtil.dateAdd(DatePart.DAY, newRowEffective, -1);
                    currentCopy.set("expire_timestamp", dateOneDayBefore);
                    layout.duplicates.push(currentCopy);
                })
                layout.sourceRateExceptions.setRowsAndMode(DataSourceMode.NONE, [newModelRow, ...layout.duplicates], null);
            },
            doAfterSlideIn: () => {
                layout.tableRateExceptions.rows.slice(1).forEach((row: TableRow) => row.findComponentById("textboxExpirationDate").color = "McLeodAlert");
            },
            fillVerticalSpace: true,
            transitionOptions: { direction: Alignment.RIGHT },
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            title: "Duplicate Rate Exception",
            addlComponents: [
                new Button({
                    id: "buttonDecline",
                    caption: "Decline",
                    fontSize: "medium",
                    backgroundColor: "primary.reverse",
                    onClick: async (event: ClickEvent) => {
                        layout.slideOut().then(() => {
                            this.tableRateExceptions.deleteRow(this.tableRateExceptions.allRows.length-1);
                            this.tableRateExceptions.fireContentsChangedListener(new TableContentsChangedEvent(this.tableRateExceptions, TableAction.DELETE));
                        });
                    },
                    color: "primary",
                    minWidth: 128,
                    borderWidth: 0,
                    rowBreak: false
                }),
                new Button({
                    id: "buttonAccept",
                    caption: "Accept",
                    fontSize: "medium",
                    backgroundColor: "primary",
                    onClick: async (event: ClickEvent) => {
                        layout.slideOut().then(() => {
                            currentRows.forEach((currentRow: TableRow, index: number) => {
                                (currentRow.data as ModelRow).setValues(layout.duplicates[index].data);
                            })
                            this.tableRateExceptions.fireContentsChangedListener(new TableContentsChangedEvent(this.tableRateExceptions, TableAction.UPDATE));
                        });
                    },
                    color: "primary.reverse",
                    minWidth: 128,
                    borderWidth: 0,
                    rowBreak: false
                })
            ]
        });
    }

    textboxChargeTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.gla_uid = "in " + this.validGlaUidsForRateExceptions;
    }

    textboxTariffLineItemcalcUomCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.field_code = "in " + this.validCalcUomCodesForRateExceptions;
    }

    textboxShipRegNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const shipperTextbox = tableRow.findComponentById("textboxShipRegName") as Textbox;
            const shipRegionName = tableRow.data.get("ship_reg_name");
            CommonTooltips.setTooltipContentLayout(shipperTextbox, null, "lme/dispatch/TariffRegionQuickInfo", null, { region_name: shipRegionName });
            tableRow.data.set("ship_reg_uid", event.selection.get("reg_uid"));
        }
    }

    textboxMethodOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const methodTextbox = tableRow.findComponentById("textboxMethod") as Textbox;
            const calcUomCode = tableRow.data.get("calc_uom_code");
            CommonTooltips.setTooltipContentLayout(methodTextbox, null, "lme/dispatch/ScreenCodesQuickInfo", null,
                { field_code: calcUomCode, field_name: "calc_uom_code" });
        }
    }

    textboxShipRegNameOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const shipperRegion = textbox.text;
        if (shipperRegion == undefined || StringUtil.isEmptyString(shipperRegion)) {
            textbox.text = null;
            textbox["lookupModelData"] = null;
        }
    }

    textboxConsRegNameOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const consigneeRegion = textbox.text;
        if (consigneeRegion == undefined || StringUtil.isEmptyString(consigneeRegion)) {
            textbox.text = null;
            textbox["lookupModelData"] = null;
        }
    }

    textboxConsRegNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const consigneeTextbox = tableRow.findComponentById("textboxConsRegName") as Textbox;
            const consigneeRegionName = tableRow.data.get("cons_reg_name");
            CommonTooltips.setTooltipContentLayout(consigneeTextbox, null, "lme/dispatch/TariffRegionQuickInfo", null, { region_name: consigneeRegionName });
            tableRow.data.set("cons_reg_uid", event.selection.get("reg_uid"));
        }
    }

    sourceRateExceptionsAfterExecution(event: DataSourceExecutionEvent) {
        this.filterPanel.toggleFilterById("active");
    }

    switchEveryOrderOnPrintable(event: PrintableEvent) {
        event.target.marginTop = event.printable ? undefined : 15;
    }

    textboxRateNoteOnPrintable(event: PrintableEvent) {
        event.target.maxHeight = event.printable ? 88 : 125;
    }

    sourceRateExceptionsAfterModeChange(event: DataSourceModeChangeEvent) {
        this.panelSecondaryFilters.visible = event.newMode !== DataSourceMode.SEARCH;
    }
}
